import { timestampFromDate } from '@bufbuild/protobuf/wkt';
import { TZDateMini } from '@date-fns/tz';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { addDays, endOfDay, isSunday, startOfDay, subDays } from 'date-fns';

import ooeConstants from '../constants';
import { State } from '../reducers';
import { deleteOrderReport } from '../shared/apis';
import { rexCateringClient } from '../shared/clients';
import { selectLocationNumber, selectLocationTimezone } from '../user/selectors';
import { getCustomerById, getOrderReport, requestOrderPayment } from './apis';
import { DashboardOrder } from './schemas';
import { cateringToDashboardDetail, cateringToDashboardOrder } from './transforms';
import {
  mapCustomerDetails,
  mapDashboardOrderDetails,
  mapDetailViewOrder,
  restaurantStartOfCalendarDay,
} from './utils';

const getDashboardOrders = async (payload: {
  locationId: string;
  startDate: Date;
  endDate: Date;
}): Promise<DashboardOrder[]> => {
  const { locationId, startDate, endDate } = payload;

  const { orders } = await rexCateringClient.getCateringOrders({
    locationId,
    startDate: timestampFromDate(startOfDay(startDate)),
    endDate: timestampFromDate(endOfDay(endDate)),
  });

  return orders.map((order) => cateringToDashboardOrder.parse(order));
};

export const getOrdersForSpecificDays = createAsyncThunk(
  'dashboard/getOrdersForSpecificDays',
  async (payload: { startDate: Date; endDate: Date }, thunkAPI) => {
    const { getState } = thunkAPI;
    const state = getState() as State;
    const locationId = selectLocationNumber(state);
    const orders = await getDashboardOrders({ locationId, ...payload });
    return { orders };
  },
);

export const getRecentAndUpcomingOrders = createAsyncThunk(
  'dashboard/getRecentAndUpcomingOrders',
  async (_, thunkAPI) => {
    const { getState } = thunkAPI;
    const state = getState() as State;
    const locationId = selectLocationNumber(state);
    const timeZone = selectLocationTimezone(state);

    const now = TZDateMini.tz(timeZone);
    let startDate = subDays(now, ooeConstants.NUM_ADDTL_DASHBOARD_PAST_ORDERS_TO_LOAD);
    if (isSunday(startDate)) {
      startDate = subDays(startDate, 1);
    }

    const endDate = addDays(now, 500);
    const orders = await getDashboardOrders({ locationId, startDate, endDate });
    const cutoffTZDate = subDays(restaurantStartOfCalendarDay(now, timeZone), 1);
    return {
      orders,
      cutoffDate: new Date(cutoffTZDate.getFullYear(), cutoffTZDate.getMonth(), cutoffTZDate.getDate()),
    };
  },
);

export const getAdditionalPastOrders = createAsyncThunk(
  'dashboard/getAdditionalPastOrders',
  async (payload: { startDate: Date; endDate: Date }, thunkAPI) => {
    const { getState } = thunkAPI;
    const state = getState() as State;
    const locationId = selectLocationNumber(state);
    const orders = await getDashboardOrders({ locationId, ...payload });
    return { orders };
  },
);

export const cancelOrder = createAsyncThunk(
  'dashboard/cancelOrder',
  async (payload: { orderId: string; cfaId: string }, thunkAPI) => {
    const { getState } = thunkAPI;
    const state = getState() as State;
    const locationNumber = selectLocationNumber(state);
    await deleteOrderReport({ locationNumber, ...payload });
  },
);

export const lookupPosOrderDetails = createAsyncThunk(
  'dashboard/lookupPosOrderDetails',
  async (payload: { orderId: string }, thunkAPI) => {
    const { getState } = thunkAPI;
    const state = getState() as State;
    const locationNumber = selectLocationNumber(state);

    const { order } = await rexCateringClient.getCateringOrderDetail({
      locationId: locationNumber,
      transId: payload.orderId,
    });
    if (!order) {
      throw new Error('Order detail not found');
    }

    return { order: cateringToDashboardDetail.parse(order) };
  },
);

export const lookupOrderDetails = createAsyncThunk(
  'dashboard/lookupOrderDetails',
  async (payload: { orderId: string; cfaId: string }, thunkAPI) => {
    const { getState } = thunkAPI;
    const state = getState() as State;
    const locationNumber = selectLocationNumber(state);
    const orderReport = await getOrderReport({ locationNumber, ...payload });
    return { order: mapDetailViewOrder(orderReport) };
  },
);

export const getDataForExcelExtraction = createAsyncThunk(
  'dashboard/getDataForExcelExtraction',
  async (payload: { order: DashboardOrder }, thunkAPI) => {
    const { getState } = thunkAPI;
    const state = getState() as State;
    const locationNumber = selectLocationNumber(state);
    const { order } = payload;

    let dashboardOrderDetails: Partial<DashboardOrder> = {};
    if (order.clientId === 'POS') {
      const { order: orderDetail } = await rexCateringClient.getCateringOrderDetail({
        locationId: locationNumber,
        transId: order.transId ?? '',
      });
      dashboardOrderDetails = cateringToDashboardDetail.parse(orderDetail);
    } else {
      const response = await getOrderReport({ locationNumber, orderId: order.id, cfaId: order.cfaId ?? '' });
      dashboardOrderDetails = mapDashboardOrderDetails(response);
    }

    return { orderWithDetails: { ...order, ...dashboardOrderDetails } };
  },
);

export const lookupCustomerDetails = createAsyncThunk(
  'dashboard/lookupCustomerDetails',
  async (payload: { orderId: string; cfaId: string }) => {
    const { cfaId } = payload;
    const customer = await getCustomerById({ cfaId });
    return { customerDetails: mapCustomerDetails(customer) };
  },
);

export const resendPaymentEmail = createAsyncThunk(
  'dashboard/resendPaymentEmail',
  async (payload: { orderId: string; cfaId: string }, thunkAPI) => {
    const { getState } = thunkAPI;
    const state = getState() as State;
    const locationNumber = selectLocationNumber(state);
    await requestOrderPayment({ ...payload, locationNumber });
  },
);
